// src/pages/auth/Login.js - Enhanced with Biometric Autofill & reCAPTCHA v3
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getRecaptchaToken } from "../../utils/recaptcha";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  InputAdornment,
  IconButton,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Paper,
  Fade,
  Slide,
  Avatar,
  useTheme,
  alpha,
  Chip,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  Sms as SmsIcon,
  Security as SecurityIcon,
  LocalHospital as HospitalIcon,
  Shield as ShieldIcon,
  Settings as SettingsIcon,
  CheckCircle,
  Error,
  Send,
  Fingerprint as FingerprintIcon,
  Face as FaceIcon,
  PhoneAndroid as PhoneIcon,
  Computer as ComputerIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";

/* eslint-disable no-undef */
// Disable ESLint warnings for browser APIs not defined in Node.js environment
// These are standard Web APIs available in browsers

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Auth context
  const authContext = useAuth();
  const {
    login,
    complete2FALogin,
    requestSMSCode,
    loading = false,
    error = null,
    clearError,
    loginFlow,
    loginGuidance,
    pendingCredentials = false,
    isAuthenticated = false,
  } = authContext || {};

  // Form states
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // SMS tracking states
  const [smsRequestInProgress, setSmsRequestInProgress] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Biometric states
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [storedCredentials, setStoredCredentials] = useState(null);
  const [biometricType, setBiometricType] = useState("");
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);

  // Check biometric support on mount
  useEffect(() => {
    checkBiometricSupport();
    loadStoredCredentials();
  }, []);

  // Authentication check
  useEffect(() => {
    try {
      if (isAuthenticated) {
        const from = location.state?.from || "/dashboard";

        const timeoutId = setTimeout(() => {
          navigate(from, { replace: true });
        }, 100);

        return () => clearTimeout(timeoutId);
      }
    } catch (err) {
      console.error("Navigation error:", err);
    }
  }, [isAuthenticated, navigate, location.state]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Clear errors when inputs change
  useEffect(() => {
    if (localError && (username || password || smsCode)) {
      setLocalError("");
    }
    if (error && (username || password || smsCode)) {
      if (clearError && typeof clearError === "function") {
        clearError();
      }
    }
  }, [username, password, smsCode]);

  // ========== BIOMETRIC FUNCTIONS ==========

  const checkBiometricSupport = async () => {
    try {
      console.log("🔍 Checking biometric support...");

      // Check basic credential management support
      const hasCredentialAPI = "credentials" in navigator;
      const hasPasswordCredential = !!window.PasswordCredential;

      // Check WebAuthn support for platform authenticators
      let hasPlatformAuth = false;
      if (window.PublicKeyCredential) {
        try {
          hasPlatformAuth =
            await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        } catch (e) {
          console.log("Platform authenticator check failed:", e);
        }
      }

      // Detect environment and biometric type
      const userAgent = navigator.userAgent.toLowerCase();
      const isInApp =
        window.navigator.standalone ||
        userAgent.includes("wv") || // WebView indicator
        (userAgent.includes("version/") && userAgent.includes("mobile")); // Mobile Safari in app

      let detectedType = "";

      if (userAgent.includes("iphone") || userAgent.includes("ipad")) {
        detectedType = isInApp ? "Face ID / Touch ID" : "Touch ID / Face ID";
      } else if (userAgent.includes("android")) {
        detectedType = "Fingerprint / Face Unlock";
      } else if (userAgent.includes("windows")) {
        detectedType = "Windows Hello";
      } else if (userAgent.includes("mac")) {
        detectedType = "Touch ID";
      } else {
        detectedType = "Biometric Authentication";
      }

      setBiometricType(detectedType);

      // Determine overall support - be more lenient for better UX
      const isSupported = hasCredentialAPI && hasPasswordCredential;
      setBiometricSupported(isSupported);

      console.log("🔍 Biometric support check result:", {
        hasCredentialAPI,
        hasPasswordCredential,
        hasPlatformAuth,
        isInApp,
        detectedType,
        isSupported,
      });

      if (isSupported) {
        // Check if credentials are already stored
        const hasStored = await checkStoredCredentials();
        setBiometricAvailable(hasStored);

        if (hasStored) {
          console.log("✅ Stored credentials found");
        } else {
          console.log("📝 No stored credentials found");
        }
      }
    } catch (error) {
      console.error("❌ Biometric support check failed:", error);
      setBiometricSupported(false);
    }
  };

  const checkStoredCredentials = async () => {
    try {
      if (!("credentials" in navigator)) return false;

      // Check if there are stored credentials for this domain
      const credentials = await navigator.credentials.get({
        password: true,
        mediation: "optional",
      });

      return !!credentials;
    } catch (error) {
      console.log("No stored credentials found:", error);
      return false;
    }
  };

  const loadStoredCredentials = async () => {
    try {
      if (!("credentials" in navigator)) return;

      // Try to get stored credentials without user interaction
      const credential = await navigator.credentials.get({
        password: true,
        mediation: "silent", // Won't show UI, just checks if available
      });

      if (credential && credential.type === "password") {

        setStoredCredentials({
          username: credential.id,
          password: credential.password,
        });
        setUsername(credential.id);
        setBiometricAvailable(true);
      }
    } catch (error) {
      console.log("No silent credentials available:", error);
    }
  };

  const handleBiometricLogin = async () => {
    try {
      setBiometricLoading(true);
      setLocalError("");


      // Request credentials with biometric verification
      const credential = await navigator.credentials.get({
        password: true,
        mediation: "required", // This will show the biometric/password prompt
      });

      if (credential && credential.type === "password") {

        setUsername(credential.id);
        setPassword(credential.password);

        // Auto-submit the form with retrieved credentials
        const result = await login(credential.id, credential.password);

        if (result?.success) {
          const from = location.state?.from || "/dashboard";
          setTimeout(() => {
            navigate(from, { replace: true });
          }, 100);
        } else if (result?.requires_2fa) {
          setResendCooldown(60);
        } else if (result?.setup_required) {
          navigate("/auth/security-setup", {
            state: {
              setupMethods: result.setup_methods,
              user: result.user,
              setupToken: result.setup_token,
              from: location.state?.from,
            },
          });
        } else {
          setLocalError(result?.message || "Biometric login failed");
        }
      }
    } catch (error) {
      console.error("❌ Biometric login failed:", error);

      // Handle different error types with more specific messages
      if (error.name === "NotAllowedError") {
        setLocalError("Biometric authentication was cancelled");
      } else if (error.name === "NotSupportedError") {
        setLocalError("Biometric authentication is not supported");
      } else if (error.name === "SecurityError") {
        setLocalError("Biometric authentication is not available");
      } else if (error.name === "InvalidStateError") {
        setLocalError(
          "No saved credentials found. Please log in manually first."
        );
      } else {
        setLocalError(
          "Biometric authentication failed. Please try manual login."
        );
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const storeCredentialsForBiometric = async (user, pass) => {
    try {

      if (!("credentials" in navigator) || !window.PasswordCredential) {
        console.log("❌ Password Credential API not supported");
        return false;
      }

      // Create a password credential using the correct constructor format
      const credentialData = {
        id: user,
        password: pass,
        name: user, // Display name
      };

      // Only add iconURL if we have a favicon to avoid 404 errors
      try {
        const response = await fetch(`${window.location.origin}/favicon.ico`, {
          method: "HEAD",
        });
        if (response.ok) {
          credentialData.iconURL = `${window.location.origin}/favicon.ico`;
        }
      } catch (e) {
        // Ignore favicon check errors
        console.log("No favicon found, skipping iconURL");
      }

      const credential = new window.PasswordCredential(credentialData);

      // Store the credential
      await navigator.credentials.store(credential);

      setBiometricAvailable(true);
      setStoredCredentials({ username: user, password: pass });
      setShowBiometricSetup(true);

      console.log("✅ Credentials stored successfully for biometric access");

      // Auto-hide the setup notification after 5 seconds
      setTimeout(() => {
        setShowBiometricSetup(false);
      }, 5000);

      return true;
    } catch (error) {
      console.error("❌ Failed to store credentials:", error);
      console.log("Falling back to storing credentials in memory only");

      // Fallback: just store in component state for biometric availability
      setBiometricAvailable(true);
      setStoredCredentials({ username: user, password: pass });
      setShowBiometricSetup(true);

      setTimeout(() => {
        setShowBiometricSetup(false);
      }, 5000);

      return true; // Still return true for UX purposes
    }
  };

  const clearStoredCredentials = async () => {
    try {
      console.log("🗑️ Clearing stored credentials...");

      // Clear form
      setUsername("");
      setPassword("");
      setStoredCredentials(null);
      setBiometricAvailable(false);
      setShowBiometricSetup(false);

      // Note: There's no standard way to delete stored credentials from the browser
      // They will be overwritten when new credentials are stored
      console.log("✅ Stored credentials cleared from app state");
    } catch (error) {
      console.error("❌ Failed to clear credentials:", error);
    }
  };

  // ========== LOGIN FUNCTIONS ==========

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      setLocalError("Please enter both username and password");
      return;
    }

    if (!login || typeof login !== "function") {
      setLocalError(
        "Authentication service not available. Please try again later."
      );
      return;
    }

    setIsSubmitting(true);
    setLocalError("");

    try {
      // Get invisible reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken("login");

      const result = await login(username, password, recaptchaToken);


      if (result?.success) {

        // Store credentials for biometric access on successful login
        await storeCredentialsForBiometric(username, password);

        const from = location.state?.from || "/dashboard";
        const timeoutId = setTimeout(() => {
          navigate(from, { replace: true });
        }, 100);

        return () => clearTimeout(timeoutId);
      } else if (result?.requires_2fa) {
        setResendCooldown(60);

        // Store credentials even for 2FA flow (they'll be used after 2FA)
        await storeCredentialsForBiometric(username, password);
      } else if (result?.setup_required) {
        const setupMethods = result.setup_methods || [];

        if (setupMethods.includes("2fa")) {
          navigate("/auth/security-setup", {
            state: {
              setupMethods,
              user: result.user,
              setupToken: result.setup_token,
              from: location.state?.from,
            },
          });
        } else {
          setLocalError(`Security setup required: ${setupMethods.join(", ")}`);
        }
      } else if (result?.rateLimited) {
        setLocalError(
          result.message ||
            "Too many attempts. Please wait before trying again."
        );
      } else {
        const errorMessage =
          result?.message || "Login failed. Please check your credentials.";
        setLocalError(errorMessage);
      }
    } catch (err) {
      console.error("🔐 Login error:", err);
      const errorMessage = err.message || "Login failed. Please try again.";
      setLocalError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();

    if (!smsCode.trim()) {
      setLocalError("Please enter the SMS verification code");
      return;
    }

    if (!pendingCredentials) {
      setLocalError("No pending credentials. Please restart login.");
      handleBackToLogin();
      return;
    }

    if (!complete2FALogin || typeof complete2FALogin !== "function") {
      setLocalError("2FA service not available. Please try again later.");
      return;
    }

    setIsSubmitting(true);
    setLocalError("");

    try {

      const result = await complete2FALogin({
        smsCode: smsCode.trim(),
        totpCode: null,
      });


      if (result?.success) {

        // Store credentials after successful 2FA too
        if (storedCredentials) {
          await storeCredentialsForBiometric(
            storedCredentials.username,
            storedCredentials.password
          );
        }

        const from = location.state?.from || "/dashboard";
        const timeoutId = setTimeout(() => {
          navigate(from, { replace: true });
        }, 100);

        return () => clearTimeout(timeoutId);
      } else {
        const errorMessage =
          result?.message || "SMS verification failed. Please try again.";
        setLocalError(errorMessage);
        setSmsCode("");
      }
    } catch (err) {
      console.error("📱 2FA error:", err);
      const errorMessage =
        err.message || "SMS verification failed. Please try again.";
      setLocalError(errorMessage);
      setSmsCode("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendSMS = async () => {
    if (!pendingCredentials) {
      setLocalError("No pending credentials. Please restart login.");
      return;
    }

    if (!requestSMSCode || typeof requestSMSCode !== "function") {
      setLocalError("SMS service not available. Please try again later.");
      return;
    }

    if (resendCooldown > 0) {
      setLocalError(
        `Please wait ${resendCooldown} seconds before requesting another code.`
      );
      return;
    }

    if (smsRequestInProgress) {
      setLocalError("SMS request already in progress. Please wait.");
      return;
    }

    try {
      setSmsRequestInProgress(true);
      setLocalError("");

      const result = await requestSMSCode();

      if (result?.success) {
        setResendCooldown(60);
      } else {
        setLocalError(
          result?.message || "Failed to send SMS code. Please try again."
        );
      }
    } catch (err) {
      console.error("📱 SMS request error:", err);
      setLocalError(
        err.message || "Failed to send SMS code. Please try again."
      );
    } finally {
      setSmsRequestInProgress(false);
    }
  };

  const handleBackToLogin = () => {
    console.log("🔄 Going back to login form");
    setUsername("");
    setPassword("");
    setSmsCode("");
    setLocalError("");
    setResendCooldown(0);
    setSmsRequestInProgress(false);
    if (clearError && typeof clearError === "function") {
      clearError();
    }
  };

  const handleSetupRedirect = () => {
    navigate("/auth/security-setup", {
      state: {
        from: location.state?.from,
      },
    });
  };

  // Get current step for display
  const getCurrentStep = () => {
    if (loginFlow === "2fa_required") return 1;
    if (loginFlow === "setup_required") return 0;
    return 0;
  };

  // Render functions
  const renderBiometricSection = () => (
    <Box sx={{ mb: 3 }}>
      {/* Biometric Support Status */}
      {biometricSupported ? (
        <Alert severity="info" icon={<SecurityIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Biometric authentication available:</strong> {biometricType}
          </Typography>
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Biometric authentication is not supported on this device/browser
          </Typography>
        </Alert>
      )}

      {/* Biometric Setup Notification */}
      {showBiometricSetup && (
        <Fade in={true}>
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            action={
              <IconButton
                size="small"
                onClick={() => setShowBiometricSetup(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            <Typography variant="body2">
              <strong>🎉 Biometric login enabled!</strong> You can now use{" "}
              {biometricType} for future logins.
            </Typography>
          </Alert>
        </Fade>
      )}

      {/* Stored Credentials Info */}
      {storedCredentials && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Chip
            icon={<CheckCircle />}
            label={`Saved: ${storedCredentials.username}`}
            color="success"
            size="small"
          />
          <Button size="small" color="error" onClick={clearStoredCredentials}>
            Clear
          </Button>
        </Box>
      )}

      {/* Biometric Login Button */}
      {biometricSupported && biometricAvailable && (
        <Fade in={true}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleBiometricLogin}
            disabled={biometricLoading || isSubmitting || loading}
            startIcon={
              biometricLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : biometricType.includes("Face") ? (
                <FaceIcon />
              ) : (
                <FingerprintIcon />
              )
            }
            sx={{
              mb: 3,
              py: 1.5,
              borderRadius: 2,
              fontSize: "1.1rem",
              fontWeight: 600,
              background: "linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)",
              boxShadow: `0 4px 15px ${alpha("#4CAF50", 0.3)}`,
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: `0 6px 20px ${alpha("#4CAF50", 0.4)}`,
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                background: theme.palette.grey[300],
                boxShadow: "none",
                transform: "none",
              },
            }}
          >
            {biometricLoading
              ? "Authenticating..."
              : `Sign in with ${biometricType}`}
          </Button>
        </Fade>
      )}
    </Box>
  );

  const renderLoginForm = () => (
    <Fade in={true} timeout={800}>
      <Box>
        {/* Beautiful Header */}
        <Box textAlign="center" mb={4}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              margin: "0 auto 16px",
              bgcolor: theme.palette.primary.main,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            }}
          >
            <HospitalIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textAlign: "center",
            }}
          >
            MedFlowHub
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            Welcome Back
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Secure Healthcare Management System
          </Typography>
        </Box>

        {/* Biometric Section */}
        {renderBiometricSection()}

        {/* Manual Login Form */}
        <Box>
          <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
            {biometricAvailable ? "Or sign in manually" : "Sign In"}
          </Typography>

          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              required
              autoComplete="username"
              disabled={isSubmitting || loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: `0 0 0 2px ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                  },
                  "&.Mui-focused": {
                    boxShadow: `0 0 0 2px ${alpha(
                      theme.palette.primary.main,
                      0.2
                    )}`,
                  },
                },
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              disabled={isSubmitting || loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: `0 0 0 2px ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                  },
                  "&.Mui-focused": {
                    boxShadow: `0 0 0 2px ${alpha(
                      theme.palette.primary.main,
                      0.2
                    )}`,
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isSubmitting || loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={
                isSubmitting || loading || !username.trim() || !password.trim()
              }
              startIcon={
                isSubmitting || loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <LoginIcon />
                )
              }
              sx={{
                mt: 4,
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                fontSize: "1.1rem",
                fontWeight: 600,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: `0 4px 15px ${alpha(
                  theme.palette.primary.main,
                  0.3
                )}`,
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: `0 6px 20px ${alpha(
                    theme.palette.primary.main,
                    0.4
                  )}`,
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  background: theme.palette.grey[300],
                  boxShadow: "none",
                  transform: "none",
                },
              }}
            >
              {isSubmitting || loading ? "Signing In..." : "Sign In"}
            </Button>
          </form>
        </Box>
      </Box>
    </Fade>
  );

  const render2FAForm = () => (
    <Slide direction="left" in={true} timeout={600}>
      <Box>
        {/* Beautiful 2FA Header */}
        <Box textAlign="center" mb={3}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              margin: "0 auto 16px",
              bgcolor: theme.palette.secondary.main,
              background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
            }}
          >
            <ShieldIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Two-Factor Authentication
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {loginGuidance?.message ||
              "Enter the 6-digit code sent to your phone"}
          </Typography>
        </Box>

        {/* SMS Status Alert */}
        {loginGuidance?.sms_code_sent || loginGuidance?.sms_sent ? (
          <Alert
            severity="success"
            icon={<CheckCircle />}
            sx={{
              mb: 3,
              borderRadius: 2,
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            <Typography variant="body2">
              <strong>SMS code sent successfully!</strong>
              {loginGuidance?.expires_in && (
                <span>
                  {" "}
                  Code expires in {Math.floor(
                    loginGuidance.expires_in / 60
                  )}{" "}
                  minutes.
                </span>
              )}
            </Typography>
          </Alert>
        ) : (
          <Alert
            severity="info"
            icon={<Send />}
            sx={{
              mb: 3,
              borderRadius: 2,
            }}
          >
            <Typography variant="body2">
              SMS verification code has been sent to your phone automatically.
            </Typography>
          </Alert>
        )}

        <form onSubmit={handle2FASubmit}>
          <TextField
            fullWidth
            label="SMS Verification Code"
            value={smsCode}
            onChange={(e) =>
              setSmsCode(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            margin="normal"
            required
            disabled={isSubmitting || loading}
            sx={{
              "& .MuiOutlinedInput-input": {
                textAlign: "center",
                fontSize: "1.8rem",
                letterSpacing: "0.8rem",
                fontWeight: 600,
                color: theme.palette.primary.main,
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: `0 0 0 2px ${alpha(
                    theme.palette.primary.main,
                    0.1
                  )}`,
                },
                "&.Mui-focused": {
                  boxShadow: `0 0 0 2px ${alpha(
                    theme.palette.primary.main,
                    0.2
                  )}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              },
            }}
            inputProps={{
              maxLength: 6,
            }}
            placeholder="• • • • • •"
            helperText={`${smsCode.length}/6 digits entered`}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isSubmitting || loading || smsCode.length !== 6}
            startIcon={
              isSubmitting || loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SecurityIcon />
              )
            }
            sx={{
              mt: 3,
              mb: 3,
              py: 1.5,
              borderRadius: 2,
              fontSize: "1.1rem",
              fontWeight: 600,
              background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: `0 4px 15px ${alpha(
                theme.palette.secondary.main,
                0.3
              )}`,
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: `0 6px 20px ${alpha(
                  theme.palette.secondary.main,
                  0.4
                )}`,
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                background: theme.palette.grey[300],
                boxShadow: "none",
                transform: "none",
              },
            }}
          >
            {isSubmitting || loading ? "Verifying..." : "Verify & Continue"}
          </Button>

          {/* Action Buttons */}
          <Box display="flex" gap={2} justifyContent="center">
            <Button
              onClick={handleBackToLogin}
              disabled={isSubmitting || loading}
              variant="outlined"
              sx={{
                borderRadius: 2,
                minWidth: 120,
                borderColor: theme.palette.grey[300],
                color: theme.palette.text.secondary,
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              Back to Login
            </Button>

            <Button
              onClick={handleResendSMS}
              disabled={
                isSubmitting ||
                loading ||
                smsRequestInProgress ||
                resendCooldown > 0
              }
              variant="text"
              startIcon={
                smsRequestInProgress ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SmsIcon />
                )
              }
              sx={{
                borderRadius: 2,
                minWidth: 120,
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                },
                "&:disabled": {
                  color: theme.palette.grey[400],
                },
              }}
            >
              {resendCooldown > 0
                ? `Resend (${resendCooldown}s)`
                : smsRequestInProgress
                ? "Sending..."
                : "Resend Code"}
            </Button>
          </Box>

          {/* Cooldown info */}
          {resendCooldown > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Please wait {resendCooldown} seconds before requesting another
                SMS code to prevent duplicate messages.
              </Typography>
            </Alert>
          )}
        </form>
      </Box>
    </Slide>
  );

  const renderSetupRequiredForm = () => (
    <Fade in={true} timeout={800}>
      <Box textAlign="center">
        <Avatar
          sx={{
            width: 80,
            height: 80,
            margin: "0 auto 16px",
            bgcolor: theme.palette.warning.main,
            background: `linear-gradient(135deg, ${
              theme.palette.warning.main
            } 0%, ${theme.palette.orange?.main || "#ff9800"} 100%)`,
          }}
        >
          <SettingsIcon sx={{ fontSize: 40 }} />
        </Avatar>

        <Typography variant="h5" fontWeight={600} gutterBottom>
          Security Setup Required
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your account requires additional security configuration before you can
          continue.
        </Typography>

        <Alert severity="warning" sx={{ mb: 3, textAlign: "left" }}>
          <Typography variant="body2">
            <strong>Required setup:</strong>
            <br />
            • Two-factor authentication (SMS)
            <br />
            • VPN access configuration
            <br />• Personal API key generation
          </Typography>
        </Alert>

        <Button
          variant="contained"
          size="large"
          onClick={handleSetupRedirect}
          disabled={isSubmitting || loading}
          startIcon={<SettingsIcon />}
          sx={{
            mb: 2,
            py: 1.5,
            px: 4,
            borderRadius: 2,
            fontSize: "1.1rem",
            fontWeight: 600,
            background: `linear-gradient(135deg, ${
              theme.palette.warning.main
            } 0%, ${theme.palette.orange?.main || "#ff9800"} 100%)`,
          }}
        >
          Complete Security Setup
        </Button>

        <Box mt={2}>
          <Button
            onClick={handleBackToLogin}
            variant="text"
            disabled={isSubmitting || loading}
          >
            Back to Login
          </Button>
        </Box>
      </Box>
    </Fade>
  );

  // Get current error
  const currentError = localError || error;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.1
        )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Card
          elevation={24}
          sx={{
            borderRadius: 4,
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
            boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.1)}`,
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
            {/* Progress Stepper for 2FA flow */}
            {loginFlow === "2fa_required" && (
              <Box mb={4}>
                <Stepper
                  activeStep={getCurrentStep()}
                  alternativeLabel
                  sx={{
                    "& .MuiStepLabel-root .Mui-completed": {
                      color: theme.palette.success.main,
                    },
                    "& .MuiStepLabel-root .Mui-active": {
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <Step>
                    <StepLabel>Login</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>SMS Verification</StepLabel>
                  </Step>
                </Stepper>
                <Divider sx={{ mt: 3, opacity: 0.3 }} />
              </Box>
            )}

            {/* Error Display */}
            {currentError && (
              <Fade in={true}>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  }}
                  onClose={() => {
                    setLocalError("");
                    if (clearError) clearError();
                  }}
                >
                  {currentError}
                </Alert>
              </Fade>
            )}

            {/* Form Content */}
            {loginFlow === "2fa_required" && pendingCredentials
              ? render2FAForm()
              : loginFlow === "setup_required"
              ? renderSetupRequiredForm()
              : renderLoginForm()}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;
